import calendarIcon from "shared/assets/pngs/calendar-icon.png";
import contactsIcon from "shared/assets/pngs/contacts-icon.png";
import appleMailIcon from "shared/assets/svgs/apple-mail.svg";
import gmailIcon from "shared/assets/svgs/gmail.svg";

// Legacy Google OAuth gmail scopes — written by older app versions. No agent
// tools are registered under them, so they grant nothing and are ignored.
const GMAIL_READONLY_SCOPE = "https://www.googleapis.com/auth/gmail.readonly";
const GMAIL_MODIFY_SCOPE = "https://www.googleapis.com/auth/gmail.modify";

// Gmail runs over IMAP with an app password. Access is granted through these
// synthetic scopes — the same strings the agent's gmail tools check.
const GMAIL_READ_SCOPE = "google.gmail_read";
const GMAIL_WRITE_SCOPE = "google.gmail_write";

export type Value = "No Access" | "Full Access" | "Read Only";
export type Permission = {
  icon: string;
  value: Value;
  title: string;
};

const getGooglePermissions = (scopes: string[]): Permission[] => {
  const result: Permission[] = [];
  // calendar
  if (scopes.includes("https://www.googleapis.com/auth/calendar")) {
    const value: Value =
      scopes.includes("https://www.googleapis.com/auth/calendar") &&
      scopes.includes("https://www.googleapis.com/auth/calendar.readonly")
        ? "Full Access"
        : "Read Only";
    result.push({ icon: calendarIcon, title: "Calendar", value });
  } else {
    result.push({ icon: calendarIcon, title: "Calendar", value: "No Access" });
  }

  // contacts
  if (scopes.includes("https://www.googleapis.com/auth/contacts")) {
    const value: Value =
      scopes.includes("https://www.googleapis.com/auth/contacts") &&
      scopes.includes("https://www.googleapis.com/auth/contacts.readonly")
        ? "Full Access"
        : "Read Only";
    result.push({ icon: contactsIcon, title: "Contacts", value });
  } else {
    result.push({ icon: contactsIcon, title: "Contacts", value: "No Access" });
  }

  // gmail
  const gmailValue: Value = scopes.includes(GMAIL_WRITE_SCOPE)
    ? "Full Access"
    : scopes.includes(GMAIL_READ_SCOPE)
      ? "Read Only"
      : "No Access";
  result.push({ icon: gmailIcon, title: "Gmail", value: gmailValue });

  return result;
};

const icloudServices = [
  { key: "calendar", icon: calendarIcon, title: "Calendar" },
  { key: "contacts", icon: contactsIcon, title: "Contacts" },
  { key: "email", icon: appleMailIcon, title: "Email" },
];

const getIcloudPermissions = (scopes: string[]) => {
  return icloudServices.map(({ key, icon, title }): Permission => {
    // Canonical iCloud scopes are the granular `icloud.<service>_read/_write`
    // pairs — that is what the agent checks. A bare `icloud.<service>` is a
    // legacy umbrella scope written by older app versions; treat it as read
    // access so those accounts still render and heal on the next save.
    const value: Value = scopes.includes(`icloud.${key}_write`)
      ? "Full Access"
      : scopes.includes(`icloud.${key}_read`) ||
          scopes.includes(`icloud.${key}`)
        ? "Read Only"
        : "No Access";
    return { icon, title, value };
  });
};

export const getPermissions = (scopes: string[], type: "google" | "icloud") => {
  if (type === "google") return getGooglePermissions(scopes);
  return getIcloudPermissions(scopes);
};

const googleScopesMap: Record<string, Record<Value, string[]>> = {
  Gmail: {
    "Full Access": [GMAIL_READ_SCOPE, GMAIL_WRITE_SCOPE],
    "Read Only": [GMAIL_READ_SCOPE],
    "No Access": [],
  },
  Calendar: {
    "Full Access": [
      "https://www.googleapis.com/auth/calendar",
      "https://www.googleapis.com/auth/calendar.readonly",
    ],
    "Read Only": ["https://www.googleapis.com/auth/calendar"],
    "No Access": [],
  },
  Contacts: {
    "Full Access": [
      "https://www.googleapis.com/auth/contacts",
      "https://www.googleapis.com/auth/contacts.readonly",
    ],
    "Read Only": ["https://www.googleapis.com/auth/contacts"],
    "No Access": [],
  },
};

const icloudScopesMap: Record<string, Record<Value, string[]>> = {
  Email: {
    "Full Access": ["icloud.email_read", "icloud.email_write"],
    "Read Only": ["icloud.email_read"],
    "No Access": [],
  },
  Calendar: {
    "Full Access": ["icloud.calendar_read", "icloud.calendar_write"],
    "Read Only": ["icloud.calendar_read"],
    "No Access": [],
  },
  Contacts: {
    "Full Access": ["icloud.contacts_read", "icloud.contacts_write"],
    "Read Only": ["icloud.contacts_read"],
    "No Access": [],
  },
};

const getGoogleScopes = (permissions: Permission[]): string[] =>
  permissions.flatMap((p) => googleScopesMap[p.title]?.[p.value] ?? []);

const getIcloudScopes = (permissions: Permission[]): string[] =>
  permissions.flatMap((p) => icloudScopesMap[p.title]?.[p.value] ?? []);

export const getScopes = (
  permissions: Permission[],
  type: "google" | "icloud"
): string[] => {
  if (type === "google") return getGoogleScopes(permissions);
  if (type === "icloud") return getIcloudScopes(permissions);
  return [];
};

// Every scope string this app manages (current maps + legacy formats).
const MANAGED_SCOPES = new Set<string>([
  ...Object.values(googleScopesMap).flatMap((byValue) =>
    Object.values(byValue).flat()
  ),
  ...Object.values(icloudScopesMap).flatMap((byValue) =>
    Object.values(byValue).flat()
  ),
  GMAIL_MODIFY_SCOPE,
  GMAIL_READONLY_SCOPE,
  "icloud.calendar",
  "icloud.contacts",
  "icloud.email",
]);

// Scopes to persist: the selected permissions plus whatever the account
// already has that this app does not manage (openid, userinfo.*, ...).
export const buildScopes = (
  permissions: Permission[],
  type: "google" | "icloud",
  existingScopes: string[] = []
): string[] => {
  const preserved = existingScopes.filter(
    (scope) => !MANAGED_SCOPES.has(scope)
  );
  return [...new Set([...getScopes(permissions, type), ...preserved])];
};
